import React, { useState } from "react";
import { FaTimes, FaSave, FaSitemap } from "react-icons/fa";
import usePermission from "../../../Hooks/usePermission";

// InputField bileşeni dışarıda tanımlandı - re-render'da focus kaybını önler
const InputField = ({ label, name, type = "number", min, max, step, error, value, onChange, disabled = false }) => (
  <div>
    <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1">{label}</label>
    <input
      type={type}
      name={name}
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={onChange}
      disabled={disabled}
      className={`w-full px-3 py-2 text-sm border rounded-lg transition-colors ${
        disabled ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "bg-gray-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
      } ${error ? "border-red-300" : "border-gray-200"}`}
    />
    {error && <p className="text-red-500 text-[11px] mt-0.5">{error}</p>}
  </div>
);

const ProductEditForm = ({
  product,
  onSave,
  onClose,
  isLoading,
  // Bayi tarafı: fiyat, kota katsayısı ve maksimum gizli; mevcut değerleri
  // değişmeden gönderilir.
  dealerView = false,
  // "Alt firmalara da uygula" kutusu — ayarları alt firmaların aynı ürününe ve
  // onların cihazlarına aynı mantıkla yansıtır.
  allowApplyToSubFirms = true,
}) => {
  const { hasPermission } = usePermission();
  // KAYIT SÜRERKEN HİÇBİR ALAN DEĞİŞTİRİLEMEZ: istek gönderildikten sonra
  // yapılan değişiklik sunucuya gitmiyor, ekranda ise değişmiş görünüyordu —
  // kullanıcı yanlış değeri kaydettiğini sanıyordu. İzin kapıları + isLoading
  // birlikte değerlendirilir.
  const canEditSettings =
    hasPermission("firms.editProductSettings") && !isLoading;
  const canEditDistribution =
    hasPermission("firms.editProductQuotaDistribution") && !isLoading;
  const canEditWarning =
    hasPermission("firms.editProductQuotaWarning") && !isLoading;
  const [formData, setFormData] = useState({
    productName: product?.productName || "",
    productPrice: product?.productPrice || 0,
    quotaMax: product?.quotaMax || 0,
    quotaWarning: product?.quotaWarning || 0,
    syncLevel: product?.syncLevel || 0,
    quotaDistribution: product?.quotaDistribution || false,
    quotaCoefficient: product?.quotaCoefficient || 0,
    orderFrequencyDays: product?.orderFrequencyDays || 0,
    goalConsumentMonthly: product?.goalConsumentMonthly || 0,
    // Her açılışta KAPALI başlar — alt firmalara yayılım kazara yapılmasın.
    applyToSubFirms: false,
  });

  const [errors, setErrors] = useState({});

  // KAYIT SÜRERKEN MODAL KAPANMAZ: kaydetme sunucuda devam ediyor (alt
  // firmalara uygulama seçiliyse uzun sürebiliyor). Yarıda kapatmak
  // kullanıcıya "iptal ettim" hissi verirken işlem arka planda bitiyordu.
  const handleClose = () => {
    if (isLoading) return;
    onClose();
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.quotaWarning) newErrors.quotaWarning = "Zorunlu alan";
    if (dealerView) { /* maksimum bayide gizli — doğrulanmaz */ }
    else if (!formData.quotaMax) newErrors.quotaMax = "Zorunlu alan";
    else if (parseInt(formData.quotaMax) < 1000) newErrors.quotaMax = "Minimum 1000 olmalıdır";
    if (!formData.syncLevel && formData.syncLevel !== 0) newErrors.syncLevel = "Zorunlu alan";
    else if (parseInt(formData.syncLevel) < 10) newErrors.syncLevel = "Minimum 10 olmalıdır";
    else if (parseInt(formData.syncLevel) > 1000) newErrors.syncLevel = "Maksimum 1000 olmalıdır";
    if (!dealerView && (formData.productPrice === "" || formData.productPrice === undefined)) newErrors.productPrice = "Zorunlu alan";
    const orderDays = parseInt(formData.orderFrequencyDays);
    if (orderDays < 30) newErrors.orderFrequencyDays = "Minimum 30 gün olmalıdır";
    else if (orderDays > 730) newErrors.orderFrequencyDays = "Maksimum 730 gün olmalıdır";
    const goalMonthly = parseInt(formData.goalConsumentMonthly);
    if (goalMonthly < 200) newErrors.goalConsumentMonthly = "Minimum 200 olmalıdır";
    else if (goalMonthly > 100000) newErrors.goalConsumentMonthly = "Maksimum 100000 olmalıdır";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) onSave(formData);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-lg max-h-[85vh] overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100">
          <div>
            <h2 className="text-sm font-bold text-gray-800">{product?.productName}</h2>
            <p className="text-[11px] text-gray-400">Ürün Ayarlarını Düzenle</p>
          </div>
          <button
            onClick={handleClose}
            disabled={isLoading}
            title={isLoading ? "Kayıt sürüyor, lütfen bekleyin" : "Kapat"}
            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
          >
            <FaTimes className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(85vh-120px)]">
          <div className="px-5 py-4 space-y-4">
            {/* Fiyat & Katsayı */}
            {!dealerView && (
              <div className="grid grid-cols-2 gap-3">
                <InputField label="Fiyat (₺)" name="productPrice" min="0" max="1000" step="0.001" error={errors.productPrice} value={formData.productPrice} onChange={handleInputChange} disabled={!canEditSettings} />
                <InputField label="Kota Katsayısı" name="quotaCoefficient" min="0" max="100" value={formData.quotaCoefficient} onChange={handleInputChange} disabled={!canEditSettings} />
              </div>
            )}

            {/* Kota Seviyeleri */}
            <div>
              <span className="text-[11px] font-bold text-gray-600 uppercase tracking-wide">Kota Seviyeleri</span>
              <div className={`grid ${dealerView ? "grid-cols-1" : "grid-cols-2"} gap-3 mt-2`}>
                {!dealerView && (
                  <InputField label="Maximum" name="quotaMax" min="1000" max="200000" error={errors.quotaMax} value={formData.quotaMax} onChange={handleInputChange} disabled={!canEditSettings} />
                )}
                <InputField label="Uyarı" name="quotaWarning" min="100" max="5000" error={errors.quotaWarning} value={formData.quotaWarning} onChange={handleInputChange} disabled={!canEditWarning} />
              </div>
            </div>

            {/* Senkronizasyon & Sipariş */}
            <div>
              <span className="text-[11px] font-bold text-gray-600 uppercase tracking-wide">Senkronizasyon & Sipariş</span>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <InputField label="Senk. Seviyesi" name="syncLevel" min="10" max="1000" error={errors.syncLevel} value={formData.syncLevel} onChange={handleInputChange} disabled={!canEditSettings} />
                <InputField label="Sipariş Aralığı (gün)" name="orderFrequencyDays" min="30" max="730" error={errors.orderFrequencyDays} value={formData.orderFrequencyDays} onChange={handleInputChange} disabled={!canEditSettings} />
              </div>
            </div>

            {/* Hedef */}
            <InputField label="Aylık Tüketim Hedefi" name="goalConsumentMonthly" min="200" max="100000" error={errors.goalConsumentMonthly} value={formData.goalConsumentMonthly} onChange={handleInputChange} disabled={!canEditSettings} />

            {/* Toggle */}
            <div
              onClick={() => canEditDistribution && setFormData((prev) => ({ ...prev, quotaDistribution: !prev.quotaDistribution }))}
              className={`flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-200 transition-colors ${canEditDistribution ? "cursor-pointer hover:bg-slate-100" : "cursor-not-allowed opacity-60"}`}
            >
              <span className="text-xs font-semibold text-gray-700">Kota Almaya Açık/Kapalı</span>
              <div className="flex items-center gap-2">
                <span className={`text-[11px] font-bold ${formData.quotaDistribution ? "text-emerald-600" : "text-gray-400"}`}>
                  {formData.quotaDistribution ? "Açık" : "Kapalı"}
                </span>
                <div className={`w-10 h-5.5 rounded-full p-0.5 transition-colors ${formData.quotaDistribution ? "bg-emerald-500" : "bg-gray-300"}`}>
                  <div className={`w-4.5 h-4.5 bg-white rounded-full shadow transition-transform ${formData.quotaDistribution ? "translate-x-5" : "translate-x-0"}`} />
                </div>
              </div>
            </div>
            {/* Alt firmalara uygula — ayar değişikliği alt firmaların AYNI
                ürününe ve onların cihazlarına aynı mantıkla yansır. Kota
                bakiyesi kopyalanmaz; her firma kendi havuzunu korur. */}
            {allowApplyToSubFirms && canEditSettings && (
              <div
                onClick={() => {
                  if (isLoading) return;
                  setFormData((prev) => ({
                    ...prev,
                    applyToSubFirms: !prev.applyToSubFirms,
                  }));
                }}
                className={`flex items-start gap-3 p-3 rounded-xl border transition-colors cursor-pointer ${
                  formData.applyToSubFirms
                    ? "bg-violet-50 border-violet-300"
                    : "bg-gray-50 border-gray-200 hover:bg-slate-100"
                }`}
              >
                <input
                  type="checkbox"
                  name="applyToSubFirms"
                  checked={formData.applyToSubFirms}
                  onChange={handleInputChange}
                  disabled={isLoading}
                  onClick={(e) => e.stopPropagation()}
                  className="mt-0.5 w-4 h-4 shrink-0 accent-violet-600 cursor-pointer"
                />
                <div className="min-w-0">
                  <span className="flex items-center gap-1.5 text-xs font-SemiBold text-gray-700">
                    <FaSitemap className="w-3 h-3 text-violet-600 shrink-0" />
                    Alt firmalara da uygula
                  </span>
                  <p className="text-[11px] text-gray-500 mt-0.5 leading-snug">
                    Bu ürünün ayarları alt firmaların aynı ürününe birebir
                    yazılır ve onların cihazlarına da aynı şekilde yansır.
                    Firmaların kota bakiyesi kopyalanmaz.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex gap-2.5 px-5 py-3.5 border-t border-gray-100 bg-gray-50/50">
            <button
              type="button"
              onClick={handleClose}
              disabled={isLoading}
              title={isLoading ? "Kayıt sürüyor, lütfen bekleyin" : undefined}
              className="flex-1 px-4 py-2 text-xs font-semibold text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-4 py-2 text-xs font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
            >
              {isLoading ? (
                <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <FaSave className="w-3 h-3" />
              )}
              {isLoading ? "Kaydediliyor..." : "Kaydet"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductEditForm;
